// Create Sale Opportunity - Step 1
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchHorses } from '../../lib/queryFunctions';
import { useOnboarding } from '../../context/OnboardingContext';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Loader2, ArrowLeft, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

const SaleOpportunityFormPage = () => {
  const navigate = useNavigate();
  const { completeItem } = useOnboarding();
  const qc = useQueryClient();
  const [saving, setSaving] = useState(false);
  const [selectedSale, setSelectedSale] = useState('');
  const [selectedHorse, setSelectedHorse] = useState('');
  const [horseDetails, setHorseDetails] = useState(null);
  const [soldAs, setSoldAs] = useState('');
  const [propertyLine, setPropertyLine] = useState('');
  const [saleEntryNotes, setSaleEntryNotes] = useState('');
  const [entryStatus, setEntryStatus] = useState('');

  const { data: allSalesData = [], isLoading } = useQuery({
    queryKey: QK.allSales(),
    queryFn: () => api.get('/sales').then(r => r.data),
  });
  const sales = useMemo(() => {
    const openSales = allSalesData.filter(s => !s.is_closed);
    return openSales.sort((a, b) => {
      const aDate = new Date(a.start_date || a.sale_start_date || '9999-12-31');
      const bDate = new Date(b.start_date || b.sale_start_date || '9999-12-31');
      if (aDate - bDate !== 0) return aDate - bDate;
      return (a.sale_name || '').localeCompare(b.sale_name || '');
    });
  }, [allSalesData]);

  const { data: horsesData = [] } = useQuery({
    queryKey: QK.horses({ include_archived: false }),
    queryFn: fetchHorses({}),
  });
  const horses = useMemo(() =>
    [...horsesData].sort((a, b) => (a.system_name || a.name || '').localeCompare(b.system_name || b.name || '')),
  [horsesData]);

  const { data: opportunities = [] } = useQuery({
    queryKey: QK.allSaleOpportunities(),
    queryFn: () => api.get('/sale-opportunities').then(r => r.data),
  });

  const loading = isLoading;

  const fetchHorseDetails = async (horseId) => {
    try {
      const response = await api.get(`/horses/${horseId}`);
      setHorseDetails(response.data);
    } catch (error) {
      console.error('Error fetching horse:', error);
    }
  };

  const calculateAge = (horse) => {
    if (!horse.birthdate) return '-';
    const currentYear = new Date().getFullYear();
    const birthYear = new Date(horse.birthdate).getFullYear();
    const age = currentYear - birthYear;
    
    if (age === 0) {
      if (horse.still_nursing) return 'Suckling';
      if (horse.separated_from_dam) return 'Weanling';
      return 'Foal';
    }
    if (age === 1) return 'Yearling';
    return `${age}YO`;
  };

  const calculateGender = (horse) => {
    if (!horse.birthdate || !horse.sex) return '-';
    const currentYear = new Date().getFullYear();
    const birthYear = new Date(horse.birthdate).getFullYear();
    const age = currentYear - birthYear;
    
    if (horse.sex === 'female') {
      return age < 5 ? 'Filly' : 'Mare';
    } else {
      if (age >= 5) return 'Horse';
      if (horse.castrated) return 'Gelding';
      if (horse.cryptorchid) return 'Ridgeling';
      return 'Colt';
    }
  };

  const handleCreate = async () => {
    if (!selectedSale || !selectedHorse) {
      toast.error('Please select both a sale and horse');
      return;
    }

    // Check if this horse is already entered in this sale (client-side check)
    const isDuplicate = opportunities.some(
      opp => opp.horse_id === selectedHorse && opp.sale_id === selectedSale
    );
    
    if (isDuplicate) {
      toast.error('This horse is already entered in the selected sale');
      return;
    }

    setSaving(true);
    try {
      const selectedSaleObj = sales.find(s => s.id === selectedSale);
      const isFasig = selectedSaleObj?.sale_company?.toLowerCase().includes('fasig');
      const payload = {
        sale_id: selectedSale,
        horse_id: selectedHorse,
        ...(soldAs ? { sold_as: soldAs } : {}),
        ...(propertyLine ? { property_line: propertyLine } : {}),
        ...(saleEntryNotes ? { sale_entry_notes: saleEntryNotes } : {}),
        ...(entryStatus && entryStatus !== 'confirmed' ? { entry_status: entryStatus } : {}),
      };
      const response = await api.post('/sale-opportunities', payload);
      qc.invalidateQueries({ queryKey: QK.allSaleOpportunities() });
      qc.invalidateQueries({ queryKey: QK.saleOpportunities() });
      qc.invalidateQueries({ queryKey: QK.historicSaleOpportunities() });
      toast.success('Sale opportunity created');
      completeItem('create_sale_entry');
      // Navigate to detail page
      navigate(`/sales/opportunities/${response.data.id}/select-deliverables`);
    } catch (error) {
      console.error('Error creating opportunity:', error);
      toast.error(error.response?.data?.detail || 'Failed to create opportunity');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <Button
            variant="ghost"
            onClick={() => navigate('/sales/opportunities')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Opportunities
          </Button>
          <h1 className="font-serif text-3xl font-semibold">Create Sale Opportunity</h1>
          <p className="text-muted-foreground mt-1">Select a sale and horse to create an opportunity</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Step 1: Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label>Select Sale *</Label>
              <Select
                value={selectedSale}
                onValueChange={setSelectedSale}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a sale..." />
                </SelectTrigger>
                <SelectContent>
                  {sales.length === 0 ? (
                    <SelectItem value="none" disabled>No active sales. Add a sale to Active Sales first.</SelectItem>
                  ) : (
                    sales.map((sale) => {
                      // Handle both field naming conventions
                      const startDate = sale.start_date || sale.sale_start_date;
                      
                      return (
                        <SelectItem key={sale.id} value={sale.id}>
                          {sale.sale_name} ({startDate ? new Date(startDate).toLocaleDateString() : 'Date TBD'})
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
              {sales.length === 0 && (
                <p className="text-xs text-amber-600">
                  No active sales found. Add a sale to your Active Sales first.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Select Horse *</Label>
              <Select
                value={selectedHorse}
                onValueChange={(value) => {
                  setSelectedHorse(value);
                  fetchHorseDetails(value);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a horse..." />
                </SelectTrigger>
                <SelectContent>
                  {horses.map((horse) => {
                    // Check if this horse is already entered in the selected sale
                    const alreadyEntered = selectedSale && opportunities.some(
                      opp => opp.horse_id === horse.id && opp.sale_id === selectedSale
                    );
                    
                    return (
                      <SelectItem 
                        key={horse.id} 
                        value={horse.id}
                        disabled={alreadyEntered}
                      >
                        {horse.system_name || horse.name || 'Unnamed Horse'} {alreadyEntered ? '(Already Entered)' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            {/* Additional fields moved from Confirm Sale Entry */}
            {selectedSale && (() => {
              const selectedSaleObj = sales.find(s => s.id === selectedSale);
              const isFasig = selectedSaleObj?.sale_company?.toLowerCase().includes('fasig');
              const isKeeneland = selectedSaleObj?.sale_company?.toLowerCase().includes('keeneland');
              return (
                <div className="space-y-4 border-t pt-4">
                  <div className="space-y-2">
                    <Label>To Be Sold As</Label>
                    <Select value={soldAs} onValueChange={setSoldAs}>
                      <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weanling">Weanling</SelectItem>
                        <SelectItem value="yearling">Yearling</SelectItem>
                        <SelectItem value="2yo">2YO</SelectItem>
                        <SelectItem value="hora">Horses of Racing Age (HORA)</SelectItem>
                        <SelectItem value="broodmare">Broodmare</SelectItem>
                        <SelectItem value="broodmare_prospect">Broodmare Prospect</SelectItem>
                        <SelectItem value="racing_broodmare_prospect">Racing/Broodmare Prospect</SelectItem>
                        <SelectItem value="racing_prospect">Racing Prospect</SelectItem>
                        <SelectItem value="stallion">Stallion</SelectItem>
                        <SelectItem value="stallion_prospect">Stallion Prospect</SelectItem>
                        <SelectItem value="racing_stallion_prospect">Racing/Stallion Prospect</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Property Line {isKeeneland ? <span className="text-muted-foreground text-xs">(recommended for Keeneland)</span> : ''}</Label>
                    <Input value={propertyLine} onChange={(e) => setPropertyLine(e.target.value)} placeholder="Identifies the seller or legal owner of the horse at the time of the sale" />
                  </div>
                  <div className="space-y-2">
                    <Label>Entry Status</Label>
                    <Select value={entryStatus || '__none__'} onValueChange={v => setEntryStatus(v === '__none__' ? '' : v)}>
                      <SelectTrigger><SelectValue placeholder="Select status (optional)" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— None —</SelectItem>
                        {isFasig ? (
                          <>
                            <SelectItem value="nominated">Nominated</SelectItem>
                            <SelectItem value="still_under_consideration">Still Under Consideration</SelectItem>
                            <SelectItem value="accepted">Accepted</SelectItem>
                            <SelectItem value="recommended_another_sale">Recommended Another Sale</SelectItem>
                            <SelectItem value="withdrawn">Withdrawn</SelectItem>
                          </>
                        ) : (
                          <>
                            <SelectItem value="entered">Entered</SelectItem>
                            <SelectItem value="withdrawn">Withdrawn</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Sale Entry Notes</Label>
                    <Textarea value={saleEntryNotes} onChange={(e) => setSaleEntryNotes(e.target.value)} placeholder="Additional notes about this sale entry..." rows={3} />
                  </div>
                </div>
              );
            })()}

            {/* Horse Preview */}
            {horseDetails && (              <div className="mt-6 p-4 bg-muted/30 rounded-lg border">
                <p className="text-sm font-medium text-muted-foreground mb-3">Horse Details:</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground">Name</p>
                    <p className="font-medium">{horseDetails.name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Color</p>
                    <p className="font-medium capitalize">{horseDetails.color || '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Gender</p>
                    <p className="font-medium">{calculateGender(horseDetails)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Age</p>
                    <p className="font-medium">{calculateAge(horseDetails)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-medium">{horseDetails.type || calculateAge(horseDetails)}</p>
                  </div>
                  {horseDetails.sex === 'female' && (
                    <div>
                      <p className="text-xs text-muted-foreground">Cover Status</p>
                      <p className="font-medium">
                        {horseDetails.current_cover_status || 'Not Covered'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/sales/opportunities')}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!selectedSale || !selectedHorse || saving}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                Create Opportunity
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </div>
    </SidebarLayout>
  );
};

export default SaleOpportunityFormPage;
